/**
 * Draggable behavior wrapper for editor mode
 *
 * Wraps a visual component and adds drag-to-move behavior.
 * Uses the BlockRenderContext's onMove callback to update position.
 */

import React, { useCallback } from 'react'
import { useBlockRenderContext } from '@/components/blocks'
import type { BlockBehaviorProps } from '../../Types'
import { useDrag } from './UseDrag'

interface DraggableProps extends BlockBehaviorProps {
  blockId: string
  children: React.ReactElement
}

/**
 * Makes a block draggable in editor mode
 *
 * @example
 * ```tsx
 * <Draggable blockId={block.id}>
 *   <PlayerVisual block={playerBlock} />
 * </Draggable>
 * ```
 */
export function Draggable({ blockId, children }: DraggableProps) {
  const context = useBlockRenderContext()

  // Destructure stable context properties for dependency tracking
  const { onMove, zoom, mode } = context

  // Transform pixel deltas to logical coordinates using zoom
  const handleMove = useCallback(
    (id: string, delta: { deltaX: number; deltaY: number }) => {
      if (!onMove) return

      const zoomFactor = zoom / 100
      const logicalDeltaX = delta.deltaX / zoomFactor
      const logicalDeltaY = delta.deltaY / zoomFactor

      onMove(id, {
        x: logicalDeltaX,
        y: logicalDeltaY,
      })
    },
    [onMove, zoom]
  )

  const { isDragging, handlers } = useDrag({
    blockId,
    onMove: handleMove,
    enabled: mode === 'editor' && !!onMove,
  })

  // Only enable dragging in editor mode
  if (mode !== 'editor' || !onMove) {
    return children
  }

  // Add drag cursor and event handler
  return (
    <div
      onMouseDown={handlers.onMouseDown}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
    >
      {children}
    </div>
  )
}
